import React from 'react';
import { Skeleton as MuiSkeleton, Box, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import spacing from '../spacing';

/**
 * Skeleton Component
 * @component
 * @description Loading skeletons with smooth animations for various content types
 * 
 * @example
 * // Simple skeleton
 * <Skeleton width={200} height={40} />
 * 
 * @example
 * // Pre-configured skeletons
 * <Skeleton type="table" rows={5} columns={4} />
 * <Skeleton type="player-card" />
 * <Skeleton type="chart" height={300} />
 * 
 * @param {string} variant - 'text' | 'rectangular' | 'circular'
 * @param {string} type - 'player-card' | 'table' | 'list' | 'chart' | 'stats'
 */

// Uses theme.palette.action.hover/selected (which flip automatically between
// light/dark) instead of hardcoded neutral hex values, so the shimmer never
// renders as a bright light-grey block against a dark surface.
const StyledSkeleton = styled(MuiSkeleton)(({ theme }) => ({
  backgroundColor: theme.palette.action.selected,
  '&::after': {
    background: `linear-gradient(90deg, transparent, ${theme.palette.action.hover}, transparent)`,
  },
}));

// Skeleton para card de jugador
const PlayerCardSkeleton = () => (
  <Card sx={{ borderRadius: spacing.borderRadius.lg }}>
    <Box sx={{ bgcolor: 'action.hover', p: 3 }}>
      <StyledSkeleton variant="text" width="60%" height={32} />
      <StyledSkeleton variant="text" width="40%" height={24} />
    </Box>
    <CardContent>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <StyledSkeleton variant="circular" width={60} height={60} sx={{ mx: 'auto' }} />
          <StyledSkeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <StyledSkeleton variant="circular" width={60} height={60} sx={{ mx: 'auto' }} />
          <StyledSkeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <StyledSkeleton variant="circular" width={60} height={60} sx={{ mx: 'auto' }} />
          <StyledSkeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
        </Box>
      </Box>
      <StyledSkeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: spacing.borderRadius.md }} />
    </CardContent>
  </Card>
);

// Skeleton para tabla
const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <Box>
    {/* Header */}
    <Box sx={{ bgcolor: 'action.hover', p: 2, display: 'flex', gap: 2 }}>
      {Array.from({ length: columns }).map((_, index) => (
        <StyledSkeleton key={index} variant="text" width={`${100 / columns}%`} height={24} />
      ))}
    </Box>
    
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box key={rowIndex} sx={{ p: 2, display: 'flex', gap: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <StyledSkeleton key={colIndex} variant="text" width={`${100 / columns}%`} height={20} />
        ))}
      </Box>
    ))}
  </Box>
);

// Skeleton para lista
const ListSkeleton = ({ items = 5 }) => (
  <Box>
    {Array.from({ length: items }).map((_, index) => (
      <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <StyledSkeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <StyledSkeleton variant="text" width="60%" height={24} />
          <StyledSkeleton variant="text" width="40%" height={20} />
        </Box>
      </Box>
    ))}
  </Box>
);

// Skeleton para gráfico
const ChartSkeleton = ({ height = 300 }) => (
  <Box sx={{ p: 3 }}>
    <StyledSkeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
    <StyledSkeleton variant="rectangular" width="100%" height={height} sx={{ borderRadius: spacing.borderRadius.md }} />
    <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
      <StyledSkeleton variant="text" width={80} height={20} />
      <StyledSkeleton variant="text" width={80} height={20} />
      <StyledSkeleton variant="text" width={80} height={20} />
    </Box>
  </Box>
);

// Skeleton para estadísticas
const StatsSkeleton = ({ stats = 3 }) => (
  <Box sx={{ display: 'flex', gap: 3, justifyContent: 'space-around' }}>
    {Array.from({ length: stats }).map((_, index) => (
      <Box key={index} sx={{ textAlign: 'center', flex: 1 }}>
        <StyledSkeleton variant="text" width={80} height={60} sx={{ mx: 'auto' }} />
        <StyledSkeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
      </Box>
    ))}
  </Box>
);

const Skeleton = ({
  variant = 'text',
  width,
  height,
  type, // 'player-card', 'table', 'list', 'chart', 'stats'
  ...props
}) => {
  // Skeletons preconfigurados
  if (type === 'player-card') {
    return <PlayerCardSkeleton {...props} />;
  }
  
  if (type === 'table') {
    return <TableSkeleton {...props} />;
  }
  
  if (type === 'list') {
    return <ListSkeleton {...props} />;
  }
  
  if (type === 'chart') {
    return <ChartSkeleton {...props} />;
  }
  
  if (type === 'stats') {
    return <StatsSkeleton {...props} />;
  }
  
  // Skeleton genérico
  return (
    <StyledSkeleton
      variant={variant}
      width={width}
      height={height}
      {...props}
    />
  );
};

export default Skeleton;
